import type { Bbox2 } from "../geo/bbox";
import { isNoData, type GeoRaster } from "./geoRaster";

export interface HeightGrid {
  /** (n+1)^2 elevations in CRS vertical units, `NaN` where the model has no data. */
  values: Float32Array;
  /** Number of cells per side; the grid is (n+1) x (n+1) samples. */
  cells: number;
  min: number;
  max: number;
  /** Fraction of samples that carry real data. */
  coverage: number;
}

/**
 * Samples a surface model on a regular grid covering `bbox`.
 *
 * Sampling is done by bilinear interpolation at exact CRS positions rather than by letting
 * the reader resample a rounded pixel window: adjacent terrain tiles then evaluate their
 * shared edge at identical coordinates and produce identical heights, which is what keeps
 * the tiled surface free of cracks.
 */
export async function sampleHeights(
  raster: GeoRaster,
  level: number,
  bbox: Bbox2,
  cells: number,
  signal?: AbortSignal,
): Promise<HeightGrid> {
  const points = cells + 1;
  const values = new Float32Array(points * points);
  values.fill(NaN);

  const corner0 = raster.crsToPixel(level, bbox.minX, bbox.maxY);
  const corner1 = raster.crsToPixel(level, bbox.maxX, bbox.minY);
  const lvl = raster.levels[level];

  // One pixel of margin on each side so bilinear taps at the tile border have neighbours.
  const x0 = Math.max(0, Math.floor(Math.min(corner0.px, corner1.px)) - 1);
  const y0 = Math.max(0, Math.floor(Math.min(corner0.py, corner1.py)) - 1);
  const x1 = Math.min(lvl.width, Math.ceil(Math.max(corner0.px, corner1.px)) + 1);
  const y1 = Math.min(lvl.height, Math.ceil(Math.max(corner0.py, corner1.py)) + 1);

  if (x1 <= x0 || y1 <= y0) {
    return { values, cells, min: NaN, max: NaN, coverage: 0 };
  }

  const window = await raster.readWindow(level, [x0, y0, x1, y1], { samples: [0], signal });
  const data = window.data as unknown as ArrayLike<number>;
  const noData = raster.noData;

  const sample = (px: number, py: number): number => {
    const cx = Math.min(window.width - 1, Math.max(0, px));
    const cy = Math.min(window.height - 1, Math.max(0, py));
    const value = data[cy * window.width + cx];
    if (value === undefined || isNoData(value, noData)) return NaN;
    return value;
  };

  let min = Infinity;
  let max = -Infinity;
  let filled = 0;

  for (let row = 0; row < points; row++) {
    // Grid rows run north to south so the resulting mesh matches the raster orientation.
    const y = bbox.maxY - ((bbox.maxY - bbox.minY) * row) / cells;
    for (let col = 0; col < points; col++) {
      const x = bbox.minX + ((bbox.maxX - bbox.minX) * col) / cells;
      const { px, py } = raster.crsToPixel(level, x, y);

      // Pixel centres sit at integer + 0.5 in this convention.
      const fx = px - 0.5 - x0;
      const fy = py - 0.5 - y0;
      const ix = Math.floor(fx);
      const iy = Math.floor(fy);
      const tx = fx - ix;
      const ty = fy - iy;

      const v00 = sample(ix, iy);
      const v10 = sample(ix + 1, iy);
      const v01 = sample(ix, iy + 1);
      const v11 = sample(ix + 1, iy + 1);

      let value: number;
      if (Number.isNaN(v00) || Number.isNaN(v10) || Number.isNaN(v01) || Number.isNaN(v11)) {
        // Do not smear a hole edge outwards: fall back to the nearest real sample only if
        // the closest tap itself has data, otherwise leave the vertex undefined.
        const nearest = sample(Math.round(fx), Math.round(fy));
        value = nearest;
      } else {
        const top = v00 + (v10 - v00) * tx;
        const bottom = v01 + (v11 - v01) * tx;
        value = top + (bottom - top) * ty;
      }

      values[row * points + col] = value;
      if (Number.isFinite(value)) {
        filled++;
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }
  }

  return {
    values,
    cells,
    min: filled ? min : NaN,
    max: filled ? max : NaN,
    coverage: filled / values.length,
  };
}

/** At most this many samples are read when summarising a whole model. */
const RANGE_SAMPLE_LIMIT = 1024;

/**
 * Elevation statistics over the whole model, read from its coarsest overview.
 *
 * The read is capped: a surface model that ships without overviews would otherwise have
 * its full resolution pulled here, which for a large site is gigabytes.
 */
export async function elevationRange(
  raster: GeoRaster,
  signal?: AbortSignal,
): Promise<{ min: number; max: number; coverage: number }> {
  const level = raster.levelCount - 1;
  const lvl = raster.levels[level];
  const scale = Math.min(1, RANGE_SAMPLE_LIMIT / Math.max(lvl.width, lvl.height));
  const window = await raster.readWindow(level, [0, 0, lvl.width, lvl.height], {
    samples: [0],
    outWidth: Math.max(1, Math.round(lvl.width * scale)),
    outHeight: Math.max(1, Math.round(lvl.height * scale)),
    signal,
  });
  const data = window.data as unknown as ArrayLike<number>;
  let min = Infinity;
  let max = -Infinity;
  let filled = 0;
  const total = window.width * window.height;
  for (let i = 0; i < total; i++) {
    const value = data[i];
    if (isNoData(value, raster.noData)) continue;
    filled++;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return { min: filled ? min : NaN, max: filled ? max : NaN, coverage: total ? filled / total : 0 };
}
